/**
 * @file        src/lib/company.js
 * @description Neutral placeholders for the issuing-company details printed on
 *              admin-side documents (tax invoice, e-ticket, hotel voucher).
 *
 *              These are NOT a company. The real issuer is per-tenant data in
 *              `site_settings` (migration 051), read by useCompany(); this object
 *              only supplies the em-dash placeholder a field falls back to when
 *              the tenant has not configured it yet.
 *
 *              This file previously held literal "Neon Holidays" values, and
 *              useCompany() overlaid ONLY the address — so every tenant printed
 *              tenant 1's legal name, email, phone, website and jurisdiction on
 *              its own tax invoices. A wrong legal entity on a financial document
 *              is a compliance problem, so a missing value must render as blank
 *              here, never as some other tenant's company.
 * @author      Kinetika Labs
 * @created     2026-07-10
 * @changelog
 *   - 2026-07-10 Claude — Initial (admin-native invoice / e-ticket).
 *   - 2026-08-10 Claude — Hardcoded Neon identity removed; now tenant-neutral
 *                          placeholders only (see useCompany + migration 051).
 *   - 2026-09-15 Claude — Statutory placeholders (gstin / pan / registrationNo /
 *     hsn / gstStateCode / terms). Same rule as every field here: a tenant that
 *     has not configured one renders the em-dash, NEVER another tenant's number.
 *     A borrowed GSTIN on a tax invoice is a far worse failure than a blank one.
 */

/** Rendered in place of a company field the tenant has not configured. */
export const UNSET = '—';

/**
 * Is this a real configured value, as opposed to the em-dash placeholder?
 * Statutory lines (GSTIN, PAN, CIN) are rendered ONLY when this is true: a
 * seller who is not GST-registered issues a bill of supply with no GSTIN at all,
 * so an empty "GST Number: —" row is worse than no row — it reads as a defect on
 * a document that is otherwise correct.
 */
export const isSet = (v) => Boolean(v) && v !== UNSET;

/**
 * The tenant's own invoice terms (Tax Management), one line each. Appended AFTER
 * the standard clauses on each document rather than replacing them — the
 * boilerplate those documents have always carried must not vanish just because a
 * tenant added a clause of its own.
 */
export const extraTerms = (terms) => String(terms || '')
  .split(/\r?\n/)
  .map((t) => t.trim())
  .filter(Boolean);

export const COMPANY = {
  name: UNSET,
  legal: UNSET,
  address: UNSET,
  email: UNSET,
  phone: UNSET,
  support: UNSET,
  web: UNSET,
  jurisdiction: UNSET,
  // Statutory identity (migration 056), printed on the tax invoice's seller
  // block. `gstStateCode` and `terms` are '' rather than '—': one is compared,
  // not printed, and the other is a list that is simply absent when unset.
  gstin: UNSET,
  pan: UNSET,
  registrationNo: UNSET,
  hsn: UNSET,
  gstStateCode: '',
  terms: '',
};
